import React from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { cn } from '@/lib/utils'

type Props = {
  title: string
  subtitle?: React.ReactNode
  children: React.ReactNode
  /** Sticky bottom area: the main call to action and a footnote under it */
  footer?: React.ReactNode
  footnote?: React.ReactNode
  /** Hide the back arrow (first screen) */
  noBack?: boolean
  /** Where the back arrow goes. Setup screens edit the Content Shop, so they go back to it */
  backTo?: string
  /** No longer shown: athletes don't see the score. Kept so callers needn't change */
  showProgress?: boolean
}

/**
 * The mobile-first frame every onboarding screen sits in: numbered step header, a column of
 * cards, and a sticky action area at the bottom so the main button is always under the thumb.
 */
const SetupShell: React.FC<Props> = ({
  title,
  subtitle,
  children,
  footer,
  footnote,
  noBack,
  backTo = '/brand-view',
}) => {
  const navigate = useNavigate()
  // The tab bar is under this footer on phones
  return (
    <div className="mx-auto w-full max-w-md pb-44 md:pb-28">
      <header className="mb-3 flex items-center gap-3 roomy:mb-4 tall:mb-5">
        {!noBack ? (
          <button
            type="button"
            onClick={() => navigate(backTo)}
            className="-ml-1 rounded-full p-1.5 text-gray-600 hover:bg-foreground/10 hover:text-foreground"
            aria-label="Back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
        ) : null}
        <h1 className="text-2xl font-bold text-foreground">{title}</h1>
      </header>

      {subtitle ? <div className="mb-5">{subtitle}</div> : null}

      <div className="space-y-3 roomy:space-y-4 tall:space-y-5">{children}</div>

      {footer ? (
        <div
          className={cn(
            'fixed inset-x-0 bottom-0 z-20 border-t border-border bg-card/95 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur-sm',
            'bottom-[calc(60px+env(safe-area-inset-bottom))] pb-4 md:bottom-0',
          )}
        >
          <div className="mx-auto w-full max-w-md">
            {footer}
            {footnote ? (
              <p className="mt-2 text-center text-xs text-gray-500">{footnote}</p>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default SetupShell

// ── Small shared pieces ────────────────────────────────────────────────────────

export const SetupCard: React.FC<
  React.HTMLAttributes<HTMLDivElement> & { highlight?: boolean }
> = ({ className, highlight, ...props }) => (
  <div
    className={cn(
      'rounded-2xl border p-4',
      highlight
        ? 'border-[#C0FD72]/60 bg-[#C0FD72]/10'
        : 'border-foreground/10 bg-card shadow-[0_1px_2px_rgba(30,41,59,0.04)]',
      className,
    )}
    {...props}
  />
)

/** Screen heading on every setup screen (24px). Card titles under it are 16px bold. */
export const SectionTitle: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <h2 className={cn('text-2xl font-semibold tracking-tight text-foreground', className)}>
    {children}
  </h2>
)

export const Eyebrow: React.FC<{ children: React.ReactNode; tone?: 'lime' | 'muted' }> = ({
  children,
  tone = 'muted',
}) => (
  <p
    className={cn(
      'text-xs font-bold uppercase tracking-wider',
      tone === 'lime' ? 'text-[#4d7c0f]' : 'text-gray-600',
    )}
  >
    {children}
  </p>
)

export const LevelBadge: React.FC<{ level: string; className?: string }> = ({
  level,
  className,
}) => (
  <span
    className={cn(
      'inline-flex h-7 min-w-7 items-center justify-center rounded-full bg-[#C0FD72] px-2 text-xs font-bold text-black',
      className,
    )}
  >
    {level}
  </span>
)

export const ScoreBar: React.FC<{ value: number; className?: string }> = ({
  value,
  className,
}) => (
  <div className={cn('h-1.5 w-full overflow-hidden rounded-full bg-muted', className)}>
    <div
      className="h-full rounded-full bg-[#C0FD72] transition-all duration-700"
      style={{ width: `${Math.max(0, Math.min(100, value))}%` }}
    />
  </div>
)

export const Chip: React.FC<{
  children: React.ReactNode
  dashed?: boolean
  active?: boolean
  onClick?: () => void
}> = ({ children, dashed, active, onClick }) => {
  const Comp: any = onClick ? 'button' : 'span'
  return (
    <Comp
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={cn(
        'inline-flex items-center rounded-full border px-3 py-1.5 text-xs font-semibold',
        dashed
          ? 'border-dashed border-foreground/25 text-gray-600'
          : 'border-foreground/15 text-gray-800',
        active && 'border-[#C0FD72] bg-[#C0FD72]/15 text-[#4d7c0f]',
        onClick && 'hover:border-foreground/40',
      )}
    >
      {children}
    </Comp>
  )
}
